import React, { useCallback, useEffect, useMemo } from 'react';
import {
  Grid,
  Box,
  styled,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { DateTime } from 'luxon';

import { useCommonTranslation } from '@/hooks/useCommonTranslation';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';

import { TripLegEventType } from '@/data/events/trip-leg';
import {
  TripLegStatus,
  TripLegStatusBC,
} from '@/data/models/trip-leg-status.model';

import * as lookupSlice from '@/data/lookup.slice';
import * as passengerSlice from '@/data/passenger.slice';
import * as uiSlice from '@/data/ui.slice';
import * as tripLegSlice from '@/data/tripleg.slice';
import {
  fetchFutureReservationsAsync,
  fetchPastReservationsAsync,
  selectPastReservations,
  selectUpcomingReservations,
} from '@/data/slices/reservation.slice';
import { TransportTripLeg } from '@/data/models/transport-reservation.model';

import reservationUtils from '@/services/reservationUtils';

import TripCard from '../shared/TripCard';

const PREFIX = 'TripsPage';

const classes = {
  container: `${PREFIX}-container`,
  header: `${PREFIX}-header`,
  tripFilterContainer: `${PREFIX}-tripFilterContainer`,
  trips: `${PREFIX}-trips`,
  tripRow: `${PREFIX}-trip-row`,
  tripMapContainer: `${PREFIX}-trip-map-container`,
  tripMapSpacer: `${PREFIX}-trip-map-spacer`,
  tripMap: `${PREFIX}-trip-map`,
  tripOuterContainer: `${PREFIX}-trip-outer-container`,
  tripHeaderContainer: `${PREFIX}-trip-header-container`,
  tripContainer: `${PREFIX}-trip-container`,
  tripItem: `${PREFIX}-trip-item`,
  tripItemConnector: `${PREFIX}-trip-item-connector`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
    ['& .section-link']: {
      textDecoration: 'none',
      color: 'unset',
    },
  },

  [`&.${classes.header}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
    background: `linear-gradient(0deg, ${theme.palette.lightActionDisabled04p.main} 0%, ${theme.palette.lightActionDisabled04p.main}  100%)`,
    padding: theme.spacing(3),
    [`& h1`]: {
      paddingTop: theme.spacing(4),
      paddingBottom: theme.spacing(1),
    },
  },

  [`&.${classes.tripFilterContainer}`]: {
    maxWidth: '394px',
    marginBottom: theme.spacing(2),
  },

  [`&.${classes.trips}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
  },
  [`&.${classes.tripRow}`]: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(3),
    flexWrap: 'wrap',
  },

  [`&.${classes.tripMapContainer}`]: {
    flex: 1,
    display: 'flex',
  },

  [`&.${classes.tripMapSpacer}`]: {
    flex: 1,
    minWidth: 20,
    [theme.breakpoints.up('md')]: {
      minWidth: 50,
    },
    [theme.breakpoints.up('lg')]: {
      minWidth: 75,
    },
    [theme.breakpoints.up('xl')]: {
      minWidth: 100,
    },
  },

  [`&.${classes.tripMap}`]: {
    width: '100%',
    minWidth: 170,
    maxWidth: 483,
  },

  [`&.${classes.tripOuterContainer}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
  },

  [`&.${classes.tripContainer}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
  },

  [`&.${classes.tripHeaderContainer}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.75),
  },

  [`&.${classes.tripItem}`]: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(1.5),
    [`& svg`]: {
      backgroundColor: theme.palette.background.default,
    },
  },

  [`&.${classes.tripItemConnector}`]: {
    width: theme.spacing(0.5),
    height: '100%',
    margin: '0 auto',
    marginTop: theme.spacing(-0.625),
    backgroundImage: "url('/dot-small.svg')",
  },
}));

export enum TripType {
  All = '1',
  Upcoming = '2',
  Past = '3',
}

const TripsPage = () => {
  const t = useCommonTranslation();
  const tripFilterButtons = [
    { label: t('member.all'), value: TripType.All },
    { label: t('member.upcoming'), value: TripType.Upcoming },
    { label: t('member.past'), value: TripType.Past },
  ];

  const dispatch = useAppDispatch();
  const selectedFilter = useAppSelector(uiSlice.selectTripsPageFilterOption);
  const passenger = useAppSelector(passengerSlice.selectCurrentPassenger);
  const timeZones = useAppSelector(lookupSlice.selectTimeZones);
  const futureReservations = useAppSelector(selectUpcomingReservations);
  const pastReservations = useAppSelector(selectPastReservations);
  const tripLegEventEmitted = useAppSelector(
    tripLegSlice.selectTripLegEventEmitted,
  );

  const handleFilterChange = (
    _event: React.MouseEvent<HTMLElement>,
    newFilter: TripType,
  ) => {
    dispatch(uiSlice.setTripsPageFilterOption(newFilter));
  };

  const fetchReservations = () => {
    if (!passenger) return;
    if (passenger.passengerUUID) {
      dispatch(
        fetchFutureReservationsAsync({
          passengerUUIDs: [passenger.passengerUUID],
          page: 1,
          pageSize: 100,
          includePassenger: true,
          includeTripLegs: true,
          sort: 'reservationServiceDate',
          excludeCanceled: true,
          reservationState: 'active',
          tripLegsState: ['active'],
          sortOrder: 'asc',
          pickupDateFrom: DateTime.local().startOf('day').toISODate(),
        }),
      );
      dispatch(
        fetchPastReservationsAsync({
          passengerUUIDs: [passenger.passengerUUID],
          page: 1,
          pageSize: 100,
          includePassenger: true,
          includeTripLegs: true,
          sort: 'reservationServiceDate',
          excludeCanceled: true,
          reservationState: 'inactive',
          tripLegsState: ['inactive'],
          tripLegStatusBCs: [TripLegStatusBC.Completed, TripLegStatusBC.NoLoad],
          pickupDateTo: DateTime.local()
            .startOf('day')
            .plus({ days: 1 }) //since it's non-inclusive we need this to include today trips
            .toISODate(),
        }),
      );
    }
  };

  useEffect(() => {
    fetchReservations();
  }, [dispatch, passenger?.passengerUUID]);

  // Realtime Event Handling
  useEffect(() => {
    // This page only needs to refetch when a trip leg is created or enters a final status
    const eventTypesToRefetchOn = [
      TripLegEventType.TripLegDrafted,
      TripLegEventType.TripLegOpened,
      TripLegEventType.TripLegCanceled,
      TripLegEventType.TripLegCanceledLate,
      TripLegEventType.TripLegCompleted,
      TripLegEventType.TripLegNotLoaded,
    ];
    if (
      tripLegEventEmitted &&
      eventTypesToRefetchOn.includes(
        tripLegEventEmitted?.detailType as TripLegEventType,
      )
    ) {
      fetchReservations();
      dispatch(tripLegSlice.setTripLegEventEmitted(null));
    }
  }, [tripLegEventEmitted, dispatch]);

  useEffect(() => {
    dispatch(lookupSlice.fetchLookupItemsAsync('timeZone'));
  }, []);

  const sortAndLimitTripLegsFromEnd = useCallback(
    (
      tripLegs: (TransportTripLeg & {
        legIndex: number;
        sortKey: number;
      })[],
      direction: 'asc' | 'desc' = 'asc',
    ) => {
      return [...(tripLegs ?? [])]
        .sort((a, b) =>
          direction === 'asc' ? a.sortKey - b.sortKey : b.sortKey - a.sortKey,
        )
        .slice(0, 100);
    },
    [],
  );

  const futureReservationsTripLegs = useMemo(() => {
    const allTripLegs = [...futureReservations.items].flatMap((reservation) =>
      (reservation.tripLegs ?? []).map((leg, legIndex) => ({
        ...leg,
        legIndex,
        sortKey: reservationUtils.getLegSortTime(leg, timeZones),
      })),
    );

    return sortAndLimitTripLegsFromEnd(allTripLegs, 'asc');
  }, [futureReservations.items, timeZones, sortAndLimitTripLegsFromEnd]);

  const pastReservationsTripLegs = useMemo(() => {
    const allTripLegs = [...pastReservations.items]
      .flatMap((reservation) =>
        (reservation.tripLegs ?? []).map((leg, legIndex) => ({
          ...leg,
          legIndex,
          sortKey: reservationUtils.getLegSortTime(leg, timeZones),
        })),
      )
      .filter(
        ({ tripLegStatusBC }) =>
          tripLegStatusBC?.toLowerCase() !== TripLegStatus.Canceled &&
          tripLegStatusBC?.toLowerCase() !== TripLegStatus.CanceledLate,
      );

    return sortAndLimitTripLegsFromEnd(allTripLegs, 'desc');
  }, [pastReservations.items, timeZones, sortAndLimitTripLegsFromEnd]);

  return (
    <>
      <StyledBox className={classes.header + ' standout-section standout-top'}>
        <Box className="standout-section-content">
          <Typography variant="h1">{t('member.trips')}</Typography>
          <StyledBox className={classes.tripFilterContainer}>
            <ToggleButtonGroup
              exclusive
              value={selectedFilter}
              onChange={(event, newValue) => {
                if (newValue !== null) {
                  handleFilterChange(event, newValue);
                }
              }}>
              {tripFilterButtons.map((button) => (
                <ToggleButton
                  key={'tftb-' + button.value}
                  value={button.value}
                  className="white-background">
                  {button.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </StyledBox>
        </Box>
      </StyledBox>
      <StyledBox className={classes.container}>
        <Grid container>
          <Grid item xs={12} md={12} lg={8} xl={9}>
            <StyledBox className={classes.container}>
              {selectedFilter === TripType.All ||
              selectedFilter === TripType.Upcoming ? (
                <>
                  {futureReservationsTripLegs.length > 0 && (
                    <Typography variant="h3">
                      {t('member.upcomingTrips')}
                    </Typography>
                  )}
                  {futureReservationsTripLegs.map(
                    ({ tripLegReferenceID, legIndex }) => (
                      <TripCard
                        key={tripLegReferenceID}
                        legIndex={legIndex}
                        tripLegReferenceID={tripLegReferenceID}
                        enableTracking={true}
                        fromTripsPage={true}
                      />
                    ),
                  )}
                </>
              ) : null}

              {selectedFilter === TripType.All ||
              selectedFilter === TripType.Past ? (
                <>
                  {pastReservationsTripLegs.length > 0 && (
                    <Typography variant="h3">
                      {t('member.pastTrips')}
                    </Typography>
                  )}
                  {pastReservationsTripLegs.map(
                    ({ tripLegReferenceID, legIndex }) => (
                      <TripCard
                        key={tripLegReferenceID}
                        legIndex={legIndex}
                        tripLegReferenceID={tripLegReferenceID}
                        enableTracking={true}
                        fromTripsPage={true}
                      />
                    ),
                  )}
                </>
              ) : null}
            </StyledBox>
          </Grid>
        </Grid>
      </StyledBox>
    </>
  );
};

export default TripsPage;
